/* oxlint-disable typescript/no-non-null-assertion */
import * as React from 'react';
import { mergeStyles } from '@cian/utils';
import { throttle } from '@cian/newbuilding-utils';
import { getCoordinates, getNodeOffset, getUniversalPosition, isClick, toggleCursorStyles } from './helpers';
import { TMouseOrTouchEvent } from './types';
import * as styles from './Thumbnails.css';

export const THUMBNAIL_LIST_EARS = 30;

export interface IThumbnailsProps {
  thumbnails: React.ReactNode[];
  activeIndex: number;
  onThumbnailClick(index: number): void;
}

/**
 * Компонент взят из библиотеки @cian/frontend-fullscreen-carousel-component@2.0.0 без изменения логики
 * и используется в т.ч. в полноэкранной галерее на карточке ЖК
 */
export class Thumbnails extends React.Component<IThumbnailsProps> {
  public elementListRef = React.createRef<HTMLDivElement>();
  public elementListOffset = 0;
  public containerRef = React.createRef<HTMLDivElement>();
  public lastCursorPosition = { x: 0, y: 0 };
  public isScrolling = false;

  public componentDidMount() {
    this.updateElementListWidth();
    this.goToElement(this.props.activeIndex);

    window.addEventListener('resize', this.throttedResizeHandler);
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.throttedResizeHandler);
  }

  public componentDidUpdate(prevProps: Readonly<IThumbnailsProps>) {
    const { activeIndex, thumbnails } = this.props;

    if (thumbnails.length !== prevProps.thumbnails.length) {
      this.updateElementListWidth();
    }

    if (activeIndex !== prevProps.activeIndex) {
      this.goToElement(activeIndex);
    }
  }

  public render() {
    const { thumbnails, activeIndex } = this.props;

    return (
      <div className={styles['layout']}>
        <div
          className={styles['container']}
          onMouseDown={this.handleMoveStartPersisted}
          onTouchStart={this.handleMoveStartPersisted}
          ref={this.containerRef}
          data-testid="ThumbnailsContainer"
        >
          <div ref={this.elementListRef} className={styles['element_list']} data-testid="ThumbnailsElementsList">
            {thumbnails.map((node, index) => (
              <div
                {...mergeStyles(styles['element'], activeIndex === index && styles['element--active'])}
                onClick={this.onThumbnailClick(index)}
                key={index}
              >
                {node}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  private handleMoveStart = (e: React.SyntheticEvent) => {
    this.toggleMove(true);
    this.lastCursorPosition = getUniversalPosition(e.nativeEvent as TMouseOrTouchEvent);
  };

  private handleResize = () => {
    this.goToElement(this.props.activeIndex);
  };

  public throttledHandleMoveStart = throttle(100, this.handleMoveStart);

  public throttedResizeHandler = throttle(100, this.handleResize);

  public handleMoveStartPersisted = (e: React.SyntheticEvent) => {
    e.persist();

    this.throttledHandleMoveStart(e);
  };

  private toggleMove = (on: boolean) => {
    const togglerFn = on ? window.addEventListener : window.removeEventListener;

    if (on) {
      this.changeSlidesContainerTransition('none');
    }
    this.isScrolling = on;
    toggleCursorStyles(on, this.elementListRef);

    togglerFn('contextmenu', this.handleMoveEnd);
    togglerFn('mouseup', this.handleMoveEnd);
    togglerFn('touchend', this.handleMoveEnd);
    togglerFn('mousemove', this.handleMove);
    togglerFn('touchmove', this.handleMove);
  };

  public changeSlidesContainerTransition = (newValue: string): void => {
    this.elementListRef.current!.style.transition = newValue;
  };

  private handleMoveEnd = (e: TMouseOrTouchEvent) => {
    /* istanbul ignore next */
    if (!this.isScrolling) {
      return;
    }

    const { deltaX } = getCoordinates(e, this.lastCursorPosition);
    this.toggleMove(false);

    const shift = this.getShift(deltaX);

    if (shift === Infinity) {
      this.goToElement(this.props.thumbnails.length - 1);
    } else if (shift === -Infinity) {
      this.goToElement(0);
    } else {
      this.setElementListOffset(shift, true);
    }
  };

  private handleMove = (e: TMouseOrTouchEvent) => {
    const { deltaX } = getCoordinates(e, this.lastCursorPosition);
    const shift = this.getShift(deltaX, true);

    if (!this.isScrolling || !isFinite(shift)) {
      return;
    }

    this.setElementListOffset(shift);
  };

  private getShift = (deltaX: number, withEars = false) => {
    const shift = this.elementListOffset + -deltaX;
    const ear = withEars ? THUMBNAIL_LIST_EARS : 0;

    if (shift > ear) {
      return -Infinity;
    }

    if (-shift >= this.elementListRef.current!.offsetWidth - this.containerRef.current!.offsetWidth + ear) {
      return Infinity;
    }

    return shift;
  };

  private goToElement = (index: number) => {
    const max = this.elementListRef.current!.offsetWidth - this.containerRef.current!.offsetWidth;

    this.changeSlidesContainerTransition(`transform ease-in-out 150ms`);

    const childNode = this.elementListRef.current!.childNodes[index];
    const offset = childNode ? getNodeOffset(childNode as HTMLDivElement) : 0;

    const target = offset > max ? max : offset;

    this.setElementListOffset(-target, true);
  };

  private setElementListOffset = (shift: number, setOffset: boolean = false) => {
    if (setOffset) {
      this.elementListOffset = shift;
    }
    this.elementListRef.current!.style.transform = `translateX(${shift}px)`;
  };

  private onThumbnailClick = (index: number) => (e: React.MouseEvent) => {
    const currentPosition = getUniversalPosition(e.nativeEvent);

    if (!isClick(this.lastCursorPosition, currentPosition)) {
      return;
    }

    this.props.onThumbnailClick(index);
  };

  private updateElementListWidth = () => {
    const elementList = this.elementListRef.current;
    const child = elementList?.childNodes[this.props.thumbnails.length - 1] as HTMLDivElement | undefined;

    /* istanbul ignore next */
    if (!elementList || !child) {
      return false;
    }

    const width = `${getNodeOffset(child as HTMLDivElement) + child.offsetWidth}px`;
    elementList.style.width = width;

    return width;
  };
}
